import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { ITaskTone, SURFACE } from './taskTones';

/**
 * Стили окна задачи (ТЗ 0064).
 *
 * Отдельный модуль, а не добавка к `TaskBoard.styled.ts`: тот и без того на
 * 950 строк и описывает доску, а это — карточка. Общие примитивы (`Chip`,
 * `EditBtn`, `DrawerLabel`) по-прежнему берутся оттуда: заводить их вторые
 * экземпляры значило бы получить два слегка разных чипа на одном экране.
 */

/**
 * Полотно окна: работа слева, свойства справа (ТЗ 0065).
 *
 * 🔴 Вкладок здесь БОЛЬШЕ НЕТ. Они прятали ровно то, ради чего окно
 * открывают: чтобы посмотреть, кто исполнитель и с какой группой связана
 * задача, приходилось переключаться туда и обратно, теряя из виду описание.
 * Аудит назвал это «административной формой», и он прав: форма спрашивает,
 * рабочее место — показывает.
 *
 * Вместе с вкладками ушла и подпорка `hidden=` вместо условного рендера:
 * она существовала только затем, чтобы черновик реплики и выбранные, но ещё
 * не прицепленные связи переживали переключение. Переключать больше нечего.
 */
export const Canvas = styled.div`
  display: grid;
  /* minmax(0, …) обязателен: без него длинное имя исполнителя или связи
     распирает колонку и ломает сетку. */
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 24px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
  }
`;

export const MainCol = styled.div`
  min-width: 0;
`;

/** Колонка свойств. Своя рамка и фон — она читается как «паспорт задачи»,
 *  а не как продолжение описания. */
export const SideCol = styled.aside`
  min-width: 0;
  border: 1px solid var(--k-border-e2e8f0);
  border-radius: 10px;
  background: var(--k-bg-f8fafc);
  padding: 14px;
`;

/**
 * Заголовок окна: чекбокс, название на месте, метки «AI» и несохранённого.
 *
 * Справа — место под крестик antd (`.ant-modal-close`, 56px поверх правого
 * поля шапки 24px): без него метка в конце строки уезжала под крестик.
 */
export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding-right: 40px;
`;

export const TitleCheck = styled.input`
  width: 17px;
  height: 17px;
  margin: 0;
  flex-shrink: 0;
  cursor: pointer;
  accent-color: var(--k-fg-2563eb);
`;

/** Название правится ПРЯМО В ШАПКЕ. Отдельное поле «Название» внутри формы
 *  дублировало заголовок окна: одна и та же строка стояла на экране дважды. */
export const TitleInput = styled.input<{ $done?: boolean }>`
  flex: 1;
  min-width: 0;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  padding: 3px 8px;
  font: inherit;
  font-size: 16px;
  font-weight: 600;
  color: var(--k-fg-0f172a);
  text-decoration: ${({ $done }) => ($done ? 'line-through' : 'none')};

  &:hover {
    border-color: var(--k-border-e2e8f0);
  }

  &:focus {
    outline: none;
    border-color: var(--k-border-2563eb);
    background: var(--k-bg-ffffff);
  }
`;

/** Одно свойство в правой колонке. */
export const Prop = styled.div`
  margin-bottom: 14px;

  &:last-child {
    margin-bottom: 0;
  }
`;

/**
 * Сегменты вместо выпадающего списка.
 *
 * Статус и приоритет меняют чаще всего остального, а `<select>` стоит трёх
 * действий: открыть, найти, выбрать. Сетка, а не строка: «Запланировано» и
 * «Отменена» в одну линию на 320px не помещаются, и колонка ехала бы вширь.
 */
export const SegGroup = styled.div<{ $cols: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $cols }) => $cols}, minmax(0, 1fr));
  gap: 3px;
  background: var(--k-bg-f1f5f9);
  border-radius: 8px;
  padding: 3px;
`;

export const Seg = styled.button<{ $active: boolean }>`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 0;
  border: 0;
  border-radius: 6px;
  padding: 5px 6px;
  font: inherit;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background: ${({ $active }) =>
    $active ? 'var(--k-bg-ffffff)' : 'transparent'};
  color: ${({ $active }) =>
    $active ? 'var(--k-fg-0f172a)' : 'var(--k-fg-64748b)'};
  box-shadow: ${({ $active }) =>
    $active ? '0 1px 3px var(--k-shadow-000000-a80)' : 'none'};
  transition: background 0.15s, color 0.15s;

  &:hover:not(:disabled) {
    color: var(--k-fg-0f172a);
  }

  &:disabled {
    cursor: default;
  }

  svg {
    display: block;
    flex-shrink: 0;
  }
`;

/** Строка опции селекта исполнителя: аватар и имя. */
export const OptionRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

export const SideDivider = styled.div`
  height: 1px;
  background: var(--k-bg-e2e8f0);
  margin: 14px -14px;
`;

export const SideHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--k-fg-475569);
  margin-bottom: 10px;
`;

/** Свёрнутая история изменений. Своя, а не общий `Disclosure`: тот помнит
 *  раскрытие в `localStorage` одним ключом на все задачи и рисует значок
 *  «инфо», который здесь врал бы про содержимое. */
export const FoldToggle = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  background: none;
  padding: 0;
  margin-top: 8px;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  color: var(--k-fg-2563eb);
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  svg {
    display: block;
  }
`;

/** Счётчик у заголовка секции — связей, реплик. */
export const TabCount = styled.span`
  min-width: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: var(--k-bg-e2e8f0);
  color: var(--k-fg-475569);
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  text-align: center;
`;

/** Две колонки полей окна создания. Не три: «Запланировано» и три
 *  приоритета в трети от 860px не помещаются. */
export const CreateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 720px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

/** Две даты рядом: начало и срок читаются парой. */
export const DateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
`;

export const Section = styled.div`
  margin-bottom: 18px;
`;

/** Строка «есть несохранённое» + кнопки. Прижата книзу окна, а не плавает
 *  внутри прокрутки: иначе о ней узнают, только долистав. */
export const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: flex-end;
`;

export const FooterNote = styled.span<{ $tone?: 'warn' | 'danger' }>`
  margin-right: auto;
  font-size: 12px;
  color: ${({ $tone }) =>
    // eslint-disable-next-line no-nested-ternary
    $tone === 'danger'
      ? 'var(--k-fg-b91c1c)'
      : $tone === 'warn'
      ? 'var(--k-fg-8a6100)'
      : 'var(--k-fg-64748b)'};
`;

/* ── Связи ───────────────────────────────────────────────────────────────── */

export const LinkGroup = styled.div`
  border: 1px solid var(--k-border-e2e8f0);
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 10px;
  background: var(--k-bg-f8fafc);
`;

export const LinkGroupHead = styled.button`
  appearance: none;
  border: 0;
  background: none;
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  color: var(--k-fg-334155);
  letter-spacing: 0.02em;
`;

export const LinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0 6px 22px;
  border-top: 1px solid var(--k-border-eef2f7);
  min-width: 0;
`;

/** Кликается ИМЯ, а не вся строка: рядом живёт крестик, и промах по строке
 *  не должен уводить со страницы. */
export const LinkName = styled.a`
  color: var(--k-fg-0f172a);
  font-size: 13px;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    color: var(--k-fg-2563eb);
    text-decoration: underline;
  }
`;

/** Та же ссылка, но роутером — связь, чей адрес собрал сервер (ТЗ 0088). */
export const LinkNameRoute = styled(Link)`
  color: var(--k-fg-0f172a);
  font-size: 13px;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    color: var(--k-fg-2563eb);
    text-decoration: underline;
  }
`;

/** Связь, которую этому человеку не открыть (раздел закрыт): приглушённая
 *  подпись без ссылки, а не «битая» ссылка (ТЗ 0088). */
export const LinkNameMuted = styled.span`
  color: var(--k-fg-64748b);
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const LinkNamePlain = styled.span`
  color: var(--k-fg-0f172a);
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const LinkHint = styled.span`
  color: var(--k-fg-64748b);
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const LinkMissing = styled.span`
  color: var(--k-fg-b91c1c);
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
`;

export const LinkRemove = styled.button`
  appearance: none;
  border: 0;
  background: none;
  cursor: pointer;
  margin-left: auto;
  padding: 2px;
  border-radius: 6px;
  color: var(--k-fg-94a3b8);
  display: inline-flex;

  &:hover {
    color: var(--k-fg-b91c1c);
    background: var(--k-bg-fee2e2);
  }
`;

/** «Показать ещё N» внутри группы связей — текстовая кнопка, а не ссылка:
 *  она ничего не открывает, только разворачивает список. */
export const MoreButton = styled.button`
  appearance: none;
  border: 0;
  background: none;
  cursor: pointer;
  padding: 6px 0 0 22px;
  font: inherit;
  font-size: 12px;
  color: var(--k-fg-2563eb);

  &:hover {
    text-decoration: underline;
  }
`;

export const AddLinkRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  flex-wrap: wrap;
  margin-top: 12px;
`;

/* ── Активность ──────────────────────────────────────────────────────────── */

export const Comment = styled.div`
  border-top: 1px solid var(--k-border-eef2f7);
  padding: 10px 0;
`;

export const CommentHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 4px;
`;

export const CommentAuthor = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: var(--k-fg-0f172a);
`;

export const CommentTime = styled.span`
  font-size: 11px;
  color: var(--k-fg-94a3b8);
`;

export const CommentBody = styled.div`
  font-size: 13px;
  color: var(--k-fg-1e293b);
  white-space: pre-wrap;
  word-break: break-word;
`;

export const CommentActions = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`;

export const Milestone = styled.div`
  display: flex;
  gap: 8px;
  align-items: baseline;
  font-size: 12px;
  color: var(--k-fg-64748b);
  padding: 4px 0;
`;

export const Empty = styled.div`
  color: var(--k-fg-94a3b8);
  font-size: 13px;
  padding: 10px 0;
`;

/* ── Описание простым текстом и автосохранение (ТЗ 0109) ─────────────────── */

/**
 * Описание как текст: переводы строк видны, длинный адрес переносится, а не
 * распирает колонку. `$clamp` — превью на карточке доски.
 */
export const PlainText = styled.div<{ $clamp?: number }>`
  font-size: ${({ $clamp }) => ($clamp ? '12px' : '14px')};
  line-height: 1.5;
  color: ${({ $clamp }) => ($clamp ? SURFACE.muted : SURFACE.text)};
  white-space: pre-line;
  word-break: break-word;
  overflow-wrap: anywhere;

  ${({ $clamp }) =>
    $clamp
      ? `
    display: -webkit-box;
    -webkit-line-clamp: ${$clamp};
    -webkit-box-orient: vertical;
    overflow: hidden;
  `
      : ''}

  a {
    /* Над накладкой названия карточки (CardTitle::after): иначе клик по
       ссылке открывал бы задачу. */
    position: relative;
    z-index: 1;
    color: ${SURFACE.accent};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

/** Индикатор автосохранения в шапке окна. */
export const SaveState = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  color: ${({ $color }) => $color};
`;

/** Строка индикатора автосохранения над полотном окна. Пустая — без места. */
export const SaveBar = styled.div`
  display: flex;
  justify-content: flex-end;
  min-height: 18px;
  margin: -8px 0 6px;
`;

/** «Не сохранено — повторить»: кнопка, выглядящая как текст индикатора. */
export const SaveRetry = styled.button<{ $color: string }>`
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  color: ${({ $color }) => $color};
  text-decoration: underline;
  cursor: pointer;
`;

/** Подсказка под полем (пустое название). */
export const FieldHint = styled.div<{ $color?: string }>`
  margin-top: 4px;
  font-size: 12px;
  color: ${({ $color }) => $color || SURFACE.muted};
`;

/**
 * Место под умные чипы связей под названием (часть B ТЗ 0109). Пустое —
 * не занимает места.
 */
export const SmartSlot = styled.div`
  &:empty {
    display: none;
  }

  margin-top: 6px;
`;

/** Ряд умных чипов связей (ТЗ 0109). */
export const SmartRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
`;

export const SmartLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: ${SURFACE.muted};
`;

/**
 * Чип подсказки. Уверенная и уже прицепленная — цветом вида связи;
 * предложение («Связать?») — серое и пунктиром: это вопрос, а не факт.
 */
export const SmartChip = styled.span<{ $tone?: ITaskTone }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  min-height: 24px;
  padding: 0 4px 0 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  border: 1px ${({ $tone }) => ($tone ? 'solid' : 'dashed')}
    ${({ $tone }) => $tone?.line ?? SURFACE.lineStrong};
  background: ${({ $tone }) => $tone?.bg ?? SURFACE.card};
  color: ${({ $tone }) => $tone?.fg ?? SURFACE.muted};

  svg {
    flex-shrink: 0;
  }
`;

/** Подсказка в чипе — сплошным цветом (без прозрачности: контраст). */
export const SmartHint = styled.span`
  font-weight: 400;
  color: ${SURFACE.muted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SmartButton = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  border: 0;
  background: transparent;
  padding: 2px 4px 2px 0;
  font: inherit;
  color: inherit;
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

export const SmartRemove = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: inherit;
  cursor: pointer;
  padding: 0;

  &:hover {
    background: var(--k-bg-0f172a-a80);
  }
`;

/** Второстепенное поле «Начало (для Ганта)» — мельче срока. */
export const SecondaryProp = styled.div`
  margin-top: 8px;

  & > *:first-child {
    font-size: 11px;
  }
`;

/* ── Файлы (ТЗ 0066) ─────────────────────────────────────────────────────── */

/**
 * Зона файлов. Перетаскивание подсвечивается рамкой: без обратной связи
 * человек не понимает, отпускать здесь или нет.
 */
export const FileZone = styled.div<{ $over?: boolean }>`
  border: 1px dashed
    ${({ $over }) =>
      $over ? 'var(--k-border-2563eb)' : 'var(--k-border-e2e8f0)'};
  border-radius: 10px;
  padding: 8px;
  background: ${({ $over }) =>
    $over ? 'var(--k-bg-2563eb-a40)' : 'var(--k-bg-ffffff)'};
  transition: border-color 0.12s ease, background 0.12s ease;
`;

export const FileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 4px;
  min-width: 0;

  & + & {
    border-top: 1px solid var(--k-border-f1f5f9);
  }
`;

export const FileName = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: none;
  padding: 0;
  font-size: 13px;
  color: var(--k-fg-1e293b);
  text-align: left;
  cursor: pointer;
  min-width: 0;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    color: var(--k-fg-2563eb);
  }

  &:disabled {
    cursor: default;
    color: var(--k-fg-94a3b8);
  }
`;

export const FileMeta = styled.span`
  font-size: 11px;
  color: var(--k-fg-94a3b8);
  white-space: nowrap;
`;

/** Пометка «файл из обсуждения» — иначе непонятно, откуда он в списке. */
export const FileFrom = styled.span`
  font-size: 11px;
  color: var(--k-fg-64748b);
  background: var(--k-bg-f1f5f9);
  border-radius: 999px;
  padding: 1px 7px;
  white-space: nowrap;
`;

export const FileActions = styled.div`
  display: flex;
  gap: 6px;
  margin-left: auto;
`;

/** Развёрнутый предпросмотр картинки. Ссылка подписанная и живёт 5 минут. */
export const FilePreview = styled.img`
  display: block;
  max-width: 100%;
  max-height: 320px;
  margin: 6px 0 2px;
  border: 1px solid var(--k-border-e2e8f0);
  border-radius: 8px;
`;

/* ── Миниатюры и просмотр картинок (ТЗ 0109) ─────────────────────────────── */

/** Сетка миниатюр картинок задачи — над строками прочих файлов. */
export const ThumbGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
  gap: 8px;
  margin-bottom: 8px;
`;

export const Thumb = styled.div`
  position: relative;
  aspect-ratio: 1 / 1;
  border: 1px solid ${SURFACE.line};
  border-radius: 8px;
  overflow: hidden;
  background: ${SURFACE.column};

  &:hover > button:last-child,
  &:focus-within > button:last-child {
    opacity: 1;
  }
`;

/** Сама миниатюра — кнопка: открывает просмотр. */
export const ThumbOpen = styled.button`
  appearance: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border: 0;
  padding: 0;
  background: transparent;
  color: ${SURFACE.mutedSoft};
  cursor: zoom-in;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &:focus-visible {
    outline: 2px solid ${SURFACE.accent};
    outline-offset: -2px;
  }
`;

/** Крестик удаления поверх миниатюры: виден по наведению и фокусу. */
export const ThumbRemove = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 50%;
  background: var(--k-bg-0f172a-a600);
  color: var(--k-fg-ffffff);
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.12s ease;

  &:focus-visible {
    opacity: 1;
  }

  /* На сенсорных экранах наведения нет — крестик виден всегда, бледно. */
  @media (hover: none) {
    opacity: 0.75;
  }

  /* Идёт удаление или загрузка — крестик гаснет и не нажимается. */
  &:disabled {
    cursor: progress;
    opacity: 0.35;
  }
`;

/** «Не удалось загрузить» на месте миниатюры (и в просмотре). */
export const ThumbFailed = styled.div<{ $inline?: boolean }>`
  ${({ $inline }) => ($inline ? '' : 'position: absolute; inset: 0;')}
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px;
  font-size: 11px;
  text-align: center;
  color: ${SURFACE.muted};
  background: ${SURFACE.column};
`;

export const ThumbRetry = styled.button`
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0;
  font: inherit;
  font-weight: 600;
  color: ${SURFACE.accent};
  text-decoration: underline;
  cursor: pointer;
`;

/** Метка «из обсуждения» на миниатюре. */
export const ThumbBadge = styled.span`
  position: absolute;
  left: 4px;
  bottom: 4px;
  max-width: calc(100% - 8px);
  padding: 0 5px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  background: var(--k-bg-0f172a-a600);
  color: var(--k-fg-ffffff);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const LightboxStage = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 240px;

  img {
    display: block;
    max-width: 100%;
    max-height: calc(100vh - 220px);
    border-radius: 6px;
  }
`;

export const LightboxNav = styled.button<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${({ $side }) => ($side === 'left' ? 'left: 0;' : 'right: 0;')}
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 50%;
  background: var(--k-bg-0f172a-a550);
  color: var(--k-fg-ffffff);
  cursor: pointer;

  &[aria-disabled='true'] {
    opacity: 0.3;
    cursor: default;
  }
`;

export const LightboxCaption = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: ${SURFACE.muted};
  text-align: center;
  word-break: break-word;
`;

/** Скрытое поле выбора файла — домашний приём (antd Upload в проекте нет). */
export const HiddenFileInput = styled.input`
  display: none;
`;
